import moment from 'moment';
import _ from 'lodash';
import { featureEnabled, MAX_NOTICE_IN_MINUTES } from 'utils';
import errorReporter from '@app/errorReporter';
import modalTemplate from './appointment_config_modal.html';
import { areAppointmentInstructionsInvalid } from './areAppointmentInstructionsInvalid';

class AppointmentConfigModalCtrl {
  constructor($rootScope, $scope, $resource, ModalService, ToastService) {
    this.AppointmentConfig = $resource('/appointments/:appointmentId/config', {
      appointmentId: '@appointmentId',
    });

    this.ValidateSentriLock = $resource(
      '/listing/:listingId/appointments/sentrilock/:lbsn',
      {
        listingId: '@listingId',
        lbsn: '@lbsn',
      },
    );

    this.getConfirmationInstructions = ({ access, instructions }) => ({
      access,
      instructions,
    });

    this.$onInit = () => {
      this.vm = ModalService.appointmentConfigModal;
    };

    this.validateSentriLock = lbsn => {
      this.validatingSentriLock = true;
      return this.ValidateSentriLock.get({
        listingId: this.vm.appointment.listingId,
        lbsn,
        date: moment().format('YYYY-MM-DD'),
      })
        .$promise.then(({ valid }) => {
          this.isSentriLockCodeValid = valid;
        })
        .catch(errorReporter.error)
        .finally(() => {
          this.validatingSentriLock = false;
        });
    };

    this.resetAccess = () => {
      this.isSentriLockCodeValid = null;
      const originalAccess = this.vm.appointmentConfig.access;
      const originalAccessName =
        this.accessInstructionNames[
          _.findIndex(this.accesses, item => item.id === originalAccess)
        ];
      const originalInstructions = {
        ...this.vm.appointmentConfig.instructions[originalAccessName],
      };

      this.accessInstructionNames.forEach(name => {
        this.vm.appointmentConfig.instructions[name] = {};
      });
      if (this.vm.appointmentConfig.access === originalAccess) {
        Object.assign(
          this.vm.appointmentConfig.instructions[originalAccessName],
          originalInstructions,
        );
      } else if (this.vm.appointmentConfig.access === 'SentriLockV2') {
        this.vm.appointmentConfig.instructions.sentrilockv2 = {
          details:
            '**ATTN - OUT OF BOARD AGENTS** please ask LA to provide you Sentriconnect access to lockbox.',
        };
      }
    };

    this.submit = form => {
      if (!form || !form.$valid) return;
      // If MasterLock deviceId is not valid
      if (
        this.vm.appointmentConfig.access === 'MasterLock' &&
        !this.vm.appointmentConfig.instructions.masterlock.deviceId
      ) {
        ToastService.add({
          type: 'warning',
          message: 'A valid Master Lock device ID must be provided.',
        });
        return;
      }
      const submit = () => {
        const totalNoticeInMinutes =
          (+this.vm.appointmentConfig.notice || 0) * 60 +
            +this.vm.appointmentConfig.noticeMinutes || 0;

        const isNoticeOutOfRange = featureEnabled('minNoticeFeature')
          ? totalNoticeInMinutes >= MAX_NOTICE_IN_MINUTES ||
            +this.vm.appointmentConfig.notice < 0 ||
            +this.vm.appointmentConfig.noticeMinutes > 60
          : +this.vm.appointmentConfig.notice < 0 ||
            +this.vm.appointmentConfig.notice > 100;

        if (isNoticeOutOfRange) {
          this.isInvalidNotice = true;
          this.vm.isSubmitting = false;
          return;
        }

        const cleanAndSave = () => {
          const partialAppointmentConfig = this.getConfirmationInstructions(
            this.vm.appointmentConfig,
          );

          const appointmentConfig = new this.AppointmentConfig({
            appointmentId: this.vm.appointment.id,
            config: partialAppointmentConfig,
          });

          appointmentConfig.$save(() => {
            this.vm.onSave();
            form.$setSubmitted();
            this.vm.isSubmitting = false;
            ModalService.closeAppointmentConfigModal();
          });
        };

        cleanAndSave();
      };

      this.vm.isSubmitting = true;
      if (this.vm.appointmentConfig.access === 'SentriLock') {
        this.validateSentriLock(
          this.vm.appointmentConfig.instructions.sentrilock.lbsn,
        )
          .then(() => {
            if (!this.isSentriLockCodeValid) {
              this.vm.isSubmitting = false;
              return;
            }
            submit();
          })
          .catch(err => {
            errorReporter.error(err);
            this.vm.isSubmitting = false;
          });
      } else {
        submit();
      }
    };

    this.areAppointmentInstructionsInvalid = () =>
      areAppointmentInstructionsInvalid(
        this.vm.appointmentConfig.access,
        this.vm.appointmentConfig.instructions,
      );

    const defaultAccesses = [
      { id: '', label: '' },
      { id: 'Lock Box', label: 'Combination Lock Box' },
      { id: 'Direct', label: 'Direct' },
      { id: 'Key', label: 'Key' },
      { id: 'Door Code', label: 'Door Code' },
    ];
    this.accesses = [...defaultAccesses];

    const defaultAccessInstructionNames = [
      'none',
      'lockbox',
      'direct',
      'key',
      'doorcode',
    ];
    this.accessInstructionNames = [...defaultAccessInstructionNames];

    this.forms = {};

    ModalService.onOpenAppointmentConfigModal(() => {
      /**
       * Constructor is ran before the hasFeature is initialized.
       * if user's org has SUPRA_MVP flag, display unassign instead of empty text.
       */
      if ($rootScope.hasFeature('SUPRA_MVP')) {
        this.accesses[0] = { id: '', label: 'Unassign' };
      }
      /**
       * Add these access options for the selected option when access in appointment config is one of them.
       */
      const accessLabelMap = {
        SentriLock: {
          label: 'SentriLock 1-Day Code',
          instructionKey: 'sentrilock',
        },
        'SentriLock App': {
          label: 'SentriLock App',
          instructionKey: 'sentrilockapp',
        },
        SentriLockV2: {
          label: 'SentriLock',
          instructionKey: 'sentrilockv2',
        },
        MasterLock: {
          label: 'Master Lock',
          instructionKey: 'masterlock',
        },
        ...($rootScope?.auth?.org.features.includes('SUPRA_MVP')
          ? {
              'Supra Lockbox': {
                label: 'Supra',
                instructionKey: 'supralockbox',
              },
            }
          : {
              'Supra eKEY': {
                label: 'Supra eKEY',
                instructionKey: 'supraekey',
              },
            }),
      };

      if (accessLabelMap[this.vm.appointmentConfig.access]) {
        const { label, instructionKey } =
          accessLabelMap[this.vm.appointmentConfig.access];

        this.accessInstructionNames.push(instructionKey);
        this.accesses.push({
          id: this.vm.appointmentConfig.access,
          label,
        });
      }
    });

    ModalService.onCloseAppointmentConfigModal(() => {
      this.accessInstructionNames = [...defaultAccessInstructionNames];
      this.accesses = [...defaultAccesses];
    });
  }
}

angular.module('bb.util').component('appointmentConfigModal', {
  bindings: {},
  controller: AppointmentConfigModalCtrl,
  template: modalTemplate,
});

AppointmentConfigModalCtrl.$inject = [
  '$rootScope',
  '$scope',
  '$resource',
  'ModalService',
  'ToastService',
];
